import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStore } from '@doctor-scheduling/web/state';
import { catchError, switchMap, throwError } from 'rxjs';

/** Auth routes that return 401 for business failures and must not trigger token refresh. */
const AUTH_NO_REFRESH_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/public/',
];

/**
 * Attaches the access token to outgoing requests and retries once after a 401 refresh.
 * Does not force-logout on transient API-down failures during Nest hot reload.
 * @param req - Outgoing HTTP request
 * @param next - Next handler in the interceptor chain
 * @returns Observable of the HTTP response stream
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const token = authStore.accessToken();

  let authReq = req;
  if (token) {
    authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  return next(authReq).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      if (AUTH_NO_REFRESH_PATHS.some((path) => req.url.includes(path))) {
        return throwError(() => error);
      }

      // No refresh token — do not loop; let the caller handle
      if (!localStorage.getItem('refresh_token')) {
        return throwError(() => error);
      }

      return authStore.refreshToken().pipe(
        switchMap((refreshed) => {
          if (refreshed) {
            const newToken = authStore.accessToken();
            const retriedReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newToken}`),
            });
            return next(retriedReq);
          }
          return throwError(() => error);
        })
      );
    })
  );
};
